import type { SyntheticEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { capitalize } from 'lodash';
import type { ApolloCache } from '@frameio/graphql-client';
import { useApolloClient } from '@frameio/graphql-client';
import type {
  Membership,
  ResourceGrantingAccessFragment,
} from '@frameio/graphql-client/generated';
import { ResourceGrantingAccess } from '@frameio/graphql-client/generated';
import { useProjectPrivacyFragment } from '@frameio/graphql-client/graphql/features/projects/fragments/generated/ProjectPrivacy';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import { EMPTY_ARRAY, imageLoader } from '@frameio/utils';
import { Box, isMobile, Text, theme } from '@frameio/vapor';
import { IconCheckmark, IconPending, IconSentFill } from '@frameio/vapor-icons';
import { useSendInviteEmail } from '../../../hooks/useSendInviteEmail';
import { useUpdateMembership } from '../../../hooks/useUpdateMembership';
import {
  getRoleNameForTracking,
  useReadMembershipFragment,
} from '../../../utils';
import { MemberEmail } from '../../MemberAccessForm/MemberEmail';
import {
  InviteAccessMemberRoleSelect,
  InviteAccessMemberRow,
} from '../InviteAccessMemberRow';
import {
  StyledDivForAdornmentTransitions,
  StyledDivForPending,
} from '../styles';
import type { InviteAccessMemberProps } from '../types';
import TEXT from './generated/TEXT/InviteAccessMember';

TEXT.DEFINE(
  {
    /** Bullet separator used in member information */
    bullet: ' • ',

    /** Text indicating membership via a user group */
    membershipFromGroup: (groupName?: string | null) =>
      groupName ? `via group – ${groupName}` : '',

    /** Text indicating inherited access from a resource type */
    inheritedAccess: (resourceType: string) =>
      `Inherited from ${capitalize(resourceType)}`,

    /** Label for role selection dropdown */
    labelRole: (memberName: string) => `Change role for ${memberName}`,

    /** Label indicating current authenticated user */
    labelAuthedUser: '(you)',

    /** Text showing single additional membership via a resource */
    otherMembership: (resourceName: string) =>
      `This user also has access via ${resourceName}`,

    /** Text showing multiple additional memberships */
    otherMemberships: (hasGroup: boolean) =>
      `This user also has access via parent resources ${
        hasGroup ? 'or user groups' : ''
      }`,

    /** Guest badge suffix for guest users */
    guest: ' · Guest',

    /** Label for pending (not yet accepted invite) user */
    pending: 'Pending User',

    /** Button text to resend invitation email */
    resendInvitation: 'Resend invite',

    /** Confirmation message shown after resending invite */
    sent: 'Invitation sent!',

    /** Menu item text to remove member access */
    removeMember: 'Remove access',

    /** Tooltip text for remove member button */
    removeMemberTooltip: 'Remove from folder',
  },
  { capitalize }
);

const MEMBER_TYPES = {
  MEMBER: 'Member',
  GUEST: 'Guest',
};

const ResendInvitationText = ({
  reinviteWasSent,
  resendButtonIsVisible,
}: {
  reinviteWasSent: boolean;
  resendButtonIsVisible: boolean;
}) => (
  <Box
    aria-atomic="true"
    aria-live="polite"
    aria-relevant="additions"
    height="100%"
    position="relative"
    role="text"
  >
    <StyledDivForAdornmentTransitions aria-hidden={!reinviteWasSent}>
      {TEXT.sent}
    </StyledDivForAdornmentTransitions>

    <StyledDivForAdornmentTransitions aria-hidden={!resendButtonIsVisible}>
      {TEXT.resendInvitation}
    </StyledDivForAdornmentTransitions>
  </Box>
);

const PendingIcons = ({
  isHovered,
  reinviteWasSent,
}: {
  isHovered?: boolean;
  reinviteWasSent?: boolean;
}) => {
  const showPending = !reinviteWasSent && !isHovered;
  const showEnvelope = !reinviteWasSent && isHovered;

  return (
    <StyledDivForPending $showBackground={showPending}>
      <StyledDivForAdornmentTransitions
        $centered={true}
        aria-hidden={!showPending}
      >
        <IconPending
          color={theme.colors.icon.secondary}
          data-testid="invite-pending-icon"
          size={16}
        />
      </StyledDivForAdornmentTransitions>

      <StyledDivForAdornmentTransitions
        $centered={true}
        aria-hidden={!showEnvelope}
      >
        <IconSentFill color={theme.colors.icon.primary} size={24} />
      </StyledDivForAdornmentTransitions>

      <StyledDivForAdornmentTransitions
        $centered={true}
        aria-hidden={!reinviteWasSent}
      >
        <IconCheckmark color={theme.colors.icon.primary} size={24} />
      </StyledDivForAdornmentTransitions>
    </StyledDivForPending>
  );
};

function readGrantingResource(
  cache: ApolloCache<any>,
  membershipId: Membership['id']
) {
  const fragment = cache.readFragment<ResourceGrantingAccessFragment>({
    id: cache.identify({ __typename: 'Membership', id: membershipId }),
    fragment: ResourceGrantingAccess,
    fragmentName: 'ResourceGrantingAccess',
  });

  return fragment ? fragment.grantingResource : null;
}

export const InviteAccessMember = ({
  accessCanBeManaged,
  availableRoles,
  createMemberships,
  currentUserId,
  deleteMembership,
  membershipId,
  queryText = '',
  backgroundColor = 'secondary',
  withSeparator,
  resourceId,
  resourceType,
  trackingSource = 'InviteModal',
}: InviteAccessMemberProps & {
  trackingSource?: TrackingSourceType;
}) => {
  const client = useApolloClient();
  const { member } = useReadMembershipFragment(membershipId);
  const grantingResource = readGrantingResource(client.cache, membershipId);

  const isProject = resourceType === 'Project';
  const { data: projectFragment } = useProjectPrivacyFragment({
    from: { __typename: 'Project', id: resourceId },
  });
  const isPending = member?.accountUser?.isPending;
  const nameToDisplay = isPending ? TEXT.pending : member?.accountUser?.name;
  const titleSuffix =
    currentUserId === member?.accountUser?.userId ? TEXT.labelAuthedUser : '';
  const guestSuffix =
    member?.accountUser?.licenseType === 'GUEST' ? ` ${TEXT.guest}` : '';

  const [reinviteWasSent, setReinviteWasSent] = useState(false);
  const [resendButtonIsVisible, setResendButtonIsVisible] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const sendInviteEmail = useSendInviteEmail();

  let otherMembershipText: string | undefined;

  if (member?.otherMemberships?.nodes[0]?.grantingResource?.name) {
    if (member.otherMemberships?.totalCount === 1) {
      otherMembershipText = TEXT.otherMembership(
        member?.otherMemberships?.nodes[0].grantingResource.name
      ) as string;
    } else {
      otherMembershipText = TEXT.otherMemberships(
        Boolean(member.otherMemberships?.nodes[0]?.accountUserGroup)
      ) as string;
    }
  }

  const onClickResend = useCallback(
    (evt: SyntheticEvent<HTMLButtonElement>) => {
      evt.stopPropagation();
      setReinviteWasSent(true);
      /**
       * Waiting a tick to make the send confirmation visible and resend button
       * hidden allows the transition to play.
       */
      requestAnimationFrame(() => {
        setResendButtonIsVisible(false);
      });
      sendInviteEmail({ membershipId });
    },
    [membershipId, sendInviteEmail]
  );

  const [updateMembership, { currentRoleId, currentRoleName }] =
    useUpdateMembership({
      availableRoles: availableRoles ?? [],
      membershipId: member?.id,
    });

  const track = useTrackUnsafe();
  const trackingData: TrackingData = useMemo(
    () => ({
      license_type:
        member?.accountUser?.licenseType !== undefined &&
        member.accountUser.licenseType !== null
          ? (MEMBER_TYPES as Record<string, string>)[
              member.accountUser.licenseType
            ]
          : undefined,
      membership_id: member?.id,
      membership_type: member?.type,
      workspace_id: isProject ? [] : [resourceId],
      project_id: isProject ? [resourceId] : [],
      source: trackingSource,
    }),
    [
      member?.accountUser?.licenseType,
      member?.id,
      member?.type,
      resourceId,
      isProject,
      trackingSource,
    ]
  );

  const handleUpdateRole = (roleId: string) => {
    updateMembership({ roleId });
    if (isResourceMembershipTrackingDataValid(trackingData)) {
      track('resource-membership-role-updated', {
        ...trackingData,
        role_name: getRoleNameForTracking(availableRoles, roleId),
      });
    }
  };

  const handleRemoveMember = useCallback(() => {
    deleteMembership?.({
      membershipId: member?.id,
      memberName: member?.accountUser?.displayName ?? '',
    });
    if (isResourceMembershipTrackingDataValid(trackingData)) {
      track('resource-membership-removed', trackingData);
    }
  }, [
    deleteMembership,
    member?.accountUser?.displayName,
    member?.id,
    track,
    trackingData,
  ]);

  return (
    <InviteAccessMemberRow
      adornmentAfter={
        availableRoles &&
        member && (
          <InviteAccessMemberRoleSelect
            accessCanBeManaged={accessCanBeManaged}
            availableRoles={availableRoles}
            label={
              member?.accountUser?.displayName
                ? (TEXT.labelRole(member?.accountUser?.displayName) as string)
                : undefined
            }
            onRemove={handleRemoveMember}
            onUpdateRole={handleUpdateRole}
            roleId={currentRoleId}
            roleName={currentRoleName}
          />
        )
      }
      adornmentBefore={
        isPending && (
          <PendingIcons
            isHovered={isHovered}
            reinviteWasSent={reinviteWasSent}
          />
        )
      }
      avatarBackground={member?.accountUser?.avatarColor ?? undefined}
      avatarSrc={
        !isPending && member?.accountUser?.profileImageUrl
          ? imageLoader({ src: member.accountUser.profileImageUrl, width: 64 })
          : undefined
      }
      backgroundColor={backgroundColor}
      buttonLabel={TEXT.resendInvitation}
      enableTooltip={
        isProject &&
        !projectFragment?.private &&
        Boolean(member?.otherMemberships?.totalCount)
      }
      hasTitlePrefix={isPending}
      metaContent={
        /**
         * @note Render no meta content for search results that have no group
         * or granting resource information.
         */
        queryText && !member?.accountUserGroup && !grantingResource ? null : (
          <MetaContent
            email={member?.accountUser?.email ?? undefined}
            groupName={member?.accountUserGroup?.name ?? undefined}
            queryText={queryText}
            resourceType={grantingResource?.resourceType}
          />
        )
      }
      onClick={
        isPending && !reinviteWasSent && createMemberships && accessCanBeManaged
          ? onClickResend
          : undefined
      }
      onHover={
        isPending && accessCanBeManaged
          ? (nextIsHovered?: boolean) => setIsHovered(Boolean(nextIsHovered))
          : undefined
      }
      queryText={queryText}
      secondaryTitle={
        queryText &&
        member?.accountUser?.email && (
          /**
           * @note We need to bump the secondary title's padding a bit when
           * a pending user invitation is re-sent to avoid text from overlapping.
           */
          <Box as="span" paddingLeft={reinviteWasSent ? 8 : undefined}>
            {TEXT.bullet}
            <Highlighter
              autoEscape={true}
              highlightStyle={{
                backgroundColor: theme.colors.background.interactive.highlight,
                color: theme.colors.text.body,
              }}
              searchWords={queryText?.split(' ') || EMPTY_ARRAY}
              textToHighlight={member?.accountUser?.email ?? ''}
            />
          </Box>
        )
      }
      title={`${nameToDisplay} ${titleSuffix}${guestSuffix}`.trim()}
      titleColor={isPending ? 'tertiary' : 'body'}
      titleOnHover={
        isPending && (
          <ResendInvitationText
            reinviteWasSent={reinviteWasSent}
            resendButtonIsVisible={resendButtonIsVisible}
          />
        )
      }
      tooltipTitle={
        member.otherMemberships?.totalCount &&
        member.otherMemberships.totalCount > 0
          ? otherMembershipText
          : undefined
      }
      withSeparator={withSeparator}
    />
  );
};

type MetaContentProps = {
  queryText?: string;
  groupName?: string;
  resourceType?: string;
  email?: string;
};

function MetaContent({
  queryText,
  groupName,
  resourceType,
  email,
}: MetaContentProps) {
  /**
   * @note If the member has access through a resource or group and is rendered as the
   * result of a search.
   */
  if (queryText && [resourceType, groupName].some(Boolean)) {
    const membershipVia = [
      TEXT.inheritedAccess(resourceType ?? ''),
      TEXT.membershipFromGroup(groupName),
    ].join(' ');
    return (
      <Text color="tertiary" truncate={true} variant="secondary">
        {resourceType ? membershipVia : TEXT.membershipFromGroup(groupName)}
      </Text>
    );
  }

  return (
    <MemberEmail
      color="tertiary"
      email={email ?? ''}
      enableTooltip={true}
      searchWords={queryText?.split(' ') || EMPTY_ARRAY}
      truncate={!isMobile}
      variant="secondary"
    />
  );
}

type TrackingData = {
  license_type?: string;
  membership_id?: string;
  membership_type?: string;
  project_id?: string[];
  workspace_id?: string[];
  source?: string;
};

const isResourceMembershipTrackingDataValid = (d: TrackingData) => {
  if (d.membership_id && d.project_id && d.workspace_id && d.source) {
    return true;
  }
  return false;
};
